import { MarkerFeature } from "../../../utils/models/MarkerFeature";
import { LineStringFeature } from "../../../utils/models/LineStringFeature";
import { IMapService, MarkerOptions, LineOptions, QueryOptions, MapBounds } from "./IMapService";
import mapboxgl from "mapbox-gl";

export class MockMapService implements IMapService {
  private markers: Map<string, { marker: MarkerFeature; options: MarkerOptions }> = new Map();
  private lines: Map<string, { line: LineStringFeature; options: LineOptions }> = new Map();
  private eventHandlers: Map<string, Function[]> = new Map();
  private center: [number, number] = [0, 0];
  private zoom: number = 10;
  private cursor: string = 'default';
  
  async addMarker(id: string, marker: MarkerFeature, options: MarkerOptions = {}): Promise<void> {
    this.markers.set(id, { marker, options });
  }
  
  updateMarkerPosition(id: string, coordinates: [number, number]): void {
    const markerData = this.markers.get(id);
    if (markerData) {
      markerData.marker.markerGeometry.coordinates = coordinates;
    }
  }
  
  removeMarker(id: string): void {
    this.markers.delete(id);
  }
  
  async addLineString(id: string, lineString: LineStringFeature, options: LineOptions = {}): Promise<void> {
    this.lines.set(id, { line: lineString, options });
  }
  
  updateLineString(id: string, lineString: LineStringFeature): void {
    const lineData = this.lines.get(id);
    if (lineData) {
      lineData.line = lineString;
    }
  }
  
  removeLineString(id: string): void {
    this.lines.delete(id);
  }
  
  queryRenderedFeatures(point: mapboxgl.PointLike, options: QueryOptions = {}): mapboxgl.MapboxGeoJSONFeature[] {
    // Mock implementation - return empty array
    return [];
  }
  
  queryRenderedFeaturesInBox(bbox: [mapboxgl.PointLike, mapboxgl.PointLike], options: QueryOptions = {}): mapboxgl.MapboxGeoJSONFeature[] {
    // Mock implementation - return empty array
    return [];
  }
  
  on(event: string, layerId: string, handler: Function): void {
    const key = `${event}:${layerId}`;
    if (!this.eventHandlers.has(key)) {
      this.eventHandlers.set(key, []);
    }
    this.eventHandlers.get(key)!.push(handler);
  }
  
  off(event: string, layerId: string, handler: Function): void {
    const key = `${event}:${layerId}`;
    const handlers = this.eventHandlers.get(key);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }
  
  once(event: string, handler: Function): void {
    // Mock implementation
  }
  
  getCenter(): [number, number] {
    return this.center;
  }
  
  getZoom(): number {
    return this.zoom;
  }
  
  getBounds(): MapBounds {
    // Mock implementation
    return {
      north: this.center[1] + 1,
      south: this.center[1] - 1,
      east: this.center[0] + 1,
      west: this.center[0] - 1
    };
  }
  
  fitBounds(bounds: MapBounds, options?: mapboxgl.FitBoundsOptions): void {
    this.center = [
      (bounds.east + bounds.west) / 2,
      (bounds.north + bounds.south) / 2
    ];
  }
  
  project(lngLat: [number, number]): { x: number; y: number } {
    // Mock implementation - simple projection
    return { x: lngLat[0] * 100, y: lngLat[1] * 100 };
  }
  
  unproject(point: { x: number; y: number }): [number, number] {
    // Mock implementation - simple unprojection
    return [point.x / 100, point.y / 100];
  }
  
  setPaintProperty(layerId: string, property: string, value: any): void {
    // Mock implementation
  }
  
  setLayoutProperty(layerId: string, property: string, value: any): void {
    // Mock implementation
  }
  
  setCursor(cursor: string): void {
    this.cursor = cursor;
  }
  
  hasLayer(layerId: string): boolean {
    return this.markers.has(layerId) || this.lines.has(layerId);
  }
  
  hasSource(sourceId: string): boolean {
    return this.hasLayer(sourceId);
  }
  
  // Test helpers
  getMarker(id: string): { marker: MarkerFeature; options: MarkerOptions } | undefined {
    return this.markers.get(id);
  }
  
  getLine(id: string): { line: LineStringFeature; options: LineOptions } | undefined {
    return this.lines.get(id);
  }
  
  getCursor(): string {
    return this.cursor;
  }
  
  triggerEvent(event: string, layerId: string, eventData: any): void {
    const key = `${event}:${layerId}`;
    const handlers = this.eventHandlers.get(key);
    if (handlers) {
      handlers.forEach(handler => handler(eventData));
    }
  }
}